import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, combineLatest, firstValueFrom, map } from 'rxjs';
import { MediaUploadService } from '../../services/media-upload.service';
import { AGMedia, AGMediaKeys, MediaContentType } from '@ag-common-lib/public-api';
import { confirm } from 'devextreme/ui/dialog';
import { ItemClickEvent } from 'devextreme/ui/tabs';
import { FILE_NAME_VALIDATION_GROUP, MediaUploaderTabs } from './ag-media-uploader-modal.models';
import { validateDxGroups } from 'ag-common-svc/lib/utils/validation';
import { base64WithoutPrefix } from './ag-media-uploader-modal.utils';

export interface FileData {
  contentType: string;
  fileName: string;
  fileSize?: number;
  base64: string;
  file: File;
}
@Injectable()
export class AgMediaUploaderModalService {
  private _file$ = new BehaviorSubject<FileData>(null);
  file$ = this._file$.asObservable();

  private readonly _inProgress$ = new BehaviorSubject<boolean>(false);
  inProgress$ = this._inProgress$.asObservable();

  private readonly _uploadProgress$ = new BehaviorSubject<number>(0);
  uploadProgress$ = this._uploadProgress$.asObservable();

  private _selectedTab$ = new BehaviorSubject<any>({ itemIndex: 0, id: MediaUploaderTabs.FromFile });
  selectedTab$ = this._selectedTab$.asObservable();
  selectedTabIndex$ = this._selectedTab$.pipe(map(tab => tab?.itemIndex));

  selectedFromGallery$ = new BehaviorSubject<AGMedia>(null);

  isFileSelected$ = combineLatest({ image: this._file$, selectedFromGallery: this.selectedFromGallery$ }).pipe(
    map(({ image, selectedFromGallery }) => {
      return !!image?.base64 || !!selectedFromGallery;
    }),
  );

  private _urlPrefix: string;
  private _waitForProcessing = false;

  constructor(
    private toastrService: ToastrService,
    private mediaUploadService: MediaUploadService,
  ) {}

  setFile = (image?: FileData) => {
    this._file$.next(image);
  };

  setSelectedFromGallery = (mediaItem: AGMedia) => {
    this.selectedFromGallery$.next(mediaItem);
  };

  setPrefix = (urlPrefix: string) => {
    this._urlPrefix = urlPrefix;
  };

  setWaitForProcessing = (waitForProcessing: boolean) => {
    this._waitForProcessing = waitForProcessing;
  };

  handleSave = async () => {
    const timingStart = this.getTimingNow();
    const tab = this._selectedTab$.value;
    const tabData = tab?.itemData;

    if (tabData?.id === MediaUploaderTabs.Gallery) {
      return this.selectedFromGallery$.value;
    }

    const isValid = await validateDxGroups(FILE_NAME_VALIDATION_GROUP);

    if (!isValid) {
      throw new Error('formValidation');
    }

    return await this.saveFile(timingStart);
  };

  private saveFile = async (timingStart?: number): Promise<AGMedia> => {
    this._inProgress$.next(true);
    this._uploadProgress$.next(0);

    const { base64, contentType, fileName, fileSize, file } = this._file$.value;
    const clearBase64 = base64WithoutPrefix(base64);
    const stats = await this.getImageDimensionsFromBase64(clearBase64, contentType);
    return this.mediaUploadService
      .uploadMedia({
        base64: base64WithoutPrefix(base64),
        file,
        filePath: this._urlPrefix,
        contentType: contentType,
        timingStart,
        waitForProcessing: this._waitForProcessing,
        onProgress: progress => {
          const safeProgress = Math.max(0, Math.min(1, Number(progress) || 0));
          this._uploadProgress$.next(safeProgress);
        },
        metadata: {
          fileName,
        },
      })
      .then(path => {
        this.toastrService.success('File Successfully Uploaded');
        this._uploadProgress$.next(1);

        return {
          [AGMediaKeys.wasabiPath]: path,
          // [AGMediaKeys.mediaType]: MediaContentType.image,
          [AGMediaKeys.fileName]: fileName,
          [AGMediaKeys.fileSize]: fileSize ?? file.size,
          [AGMediaKeys.contentType]: contentType,
          [AGMediaKeys.aspectRation]: stats?.aspectRatio ?? null,
        };
      })
      .catch(e => {
        this.toastrService.error('Failed to Upload File');
        throw new Error(e);
      })
      .finally(() => {
        this._uploadProgress$.next(0);
        this._inProgress$.next(false);
      });
  };

  private getTimingNow = (): number => {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      return performance.now();
    }
    return Date.now();
  };

  onSelectedIndexChange = async (e: ItemClickEvent) => {
    const itemIndex = e.itemIndex;
    const itemData = e?.itemData;
    const isFileSelected = await firstValueFrom(this.isFileSelected$);

    if (!isFileSelected) {
      this._selectedTab$.next({ itemIndex, itemData });
      return;
    }

    const continueWithoutSaving = await confirm(
      '<i>You are have not saved data. <br/> Are you sure you want to Switch without Saving?</i>',
      'Confirm',
    );

    if (continueWithoutSaving) {
      this.setSelectedFromGallery(null);
      this.setFile(null);
      this._selectedTab$.next({ itemIndex, itemData });
      return;
    }

    const currentTab = this._selectedTab$.value;
    this._selectedTab$.next(null);
    setTimeout(() => {
      this._selectedTab$.next(currentTab);
    }, 0);
  };

  setSelectedTab = tab => {
    this._selectedTab$.next(tab);
  };

  resetState = () => {
    this.selectedFromGallery$.next(null);
    this._file$.next(null);
    this._selectedTab$.next({
      itemIndex: 0,
      itemData: { id: MediaUploaderTabs.FromFile, text: MediaUploaderTabs.FromFile },
    });
  };

  onCancelEdit = async (onAfterRevertChanges?: () => void): Promise<boolean> => {
    this.selectedFromGallery$.next(null);
    this._file$.next(null);

    onAfterRevertChanges && onAfterRevertChanges();
    return true;
  };

  private getImageDimensionsFromBase64(
    base64: string,
    contentType: string,
  ): Promise<{ width: number; height: number; aspectRatio: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.src = `data:image/${contentType};base64,${base64}`;

      img.onload = () => {
        const width = img.width;
        const height = img.height;
        const aspectRatio = Number((width / height).toFixed(2));
        resolve({ width, height, aspectRatio });
      };

      img.onerror = err => {
        resolve(null);
      };
    });
  }
}
